import { deleteToken, getToken } from "firebase/messaging";

import { getMessagingInstance } from "@/libs/firebase";
import { DEVICE_TOKEN_STORAGE_KEY, logoutUser } from "@/libs/api/deviceToken";

/** Cross-tab channel so a logout in one tab stops every other open tab. */
export const AUTH_CHANNEL_NAME = "auth";
export const LOGOUT_MESSAGE = "logout";

const SW_URL_MARKER = "firebase-messaging-sw.js";

let loggingOut = false;

/** True once logout started in this tab — blocks late token registration. */
export const isLoggingOut = (): boolean => loggingOut;

/** Local-only cleanup: drop the cached token and stop the push worker. */
export async function clearLocalPushState(): Promise<void> {
  try {
    localStorage.removeItem(DEVICE_TOKEN_STORAGE_KEY);
  } catch {
    // storage unavailable — nothing to clear
  }
  try {
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(
        registrations
          .filter((r) =>
            [r.active, r.waiting, r.installing].some((w) =>
              w?.scriptURL.includes(SW_URL_MARKER),
            ),
          )
          .map((r) => r.unregister()),
      );
    }
  } catch (err) {
    console.error("Failed to unregister push worker:", err);
  }
}

/**
 * Revoke this browser's push subscription. Must run while the JWT is still
 * valid (the logout request is authenticated), i.e. BEFORE signOut().
 * Order: getToken -> POST /api/users/logout -> deleteToken -> local cleanup.
 * Each step is isolated so a failure never blocks the next one.
 */
export async function revokePushOnLogout(): Promise<void> {
  loggingOut = true;
  let fcmToken: string | undefined;
  let messaging = null;

  try {
    messaging = await getMessagingInstance();
    if (messaging) {
      const registration = await navigator.serviceWorker.getRegistration();
      fcmToken =
        (await getToken(messaging, {
          vapidKey: process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY,
          serviceWorkerRegistration: registration,
        })) || undefined;
    }
  } catch (err) {
    console.error("Failed to read FCM token on logout:", err);
  }

  // Fall back to the cached token if the SDK couldn't give us one.
  if (!fcmToken) {
    try {
      fcmToken = localStorage.getItem(DEVICE_TOKEN_STORAGE_KEY) ?? undefined;
    } catch {
      // ignore
    }
  }

  try {
    await logoutUser(fcmToken);
  } catch (err) {
    console.error("Failed to log out on server:", err);
  }

  try {
    if (messaging) await deleteToken(messaging);
  } catch (err) {
    console.error("Failed to delete FCM token:", err);
  }

  await clearLocalPushState();
}

/** Tell the other tabs of this browser to run their logout cleanup. */
export function broadcastLogout(): void {
  try {
    if (typeof BroadcastChannel === "undefined") return;
    const channel = new BroadcastChannel(AUTH_CHANNEL_NAME);
    channel.postMessage(LOGOUT_MESSAGE);
    channel.close();
  } catch {
    // best-effort
  }
}
